import { useEffect, useMemo, useRef, useState } from "react";
import DetailDrawer from "../DetailDrawer";
import {
  getAdminOutlets,
  getAdminSessionById,
  updateAdminSessionById,
} from "../../../services/api";
import "./SessionsView.css";

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const splitDateTimeValue = (value) => {
  const localValue = toDateTimeLocalValue(value);
  if (!localValue) {
    return { date: "", time: "" };
  }

  const [date, time] = localValue.split("T");
  return {
    date: date || "",
    time: time || "",
  };
};

const formatLocationStatusLabel = (value, t) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "on_site") {
    return t("admin.sessions.locationAudit.status.onSite", {
      defaultValue: "On Site",
    });
  }
  if (normalized === "off_site") {
    return t("admin.sessions.locationAudit.status.offSite", {
      defaultValue: "Off Site",
    });
  }
  if (!normalized) return "-";
  return t("admin.sessions.locationAudit.status.unverified", {
    defaultValue: "Unverified",
  });
};

const formatCoordinate = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(6) : "-";
};

const formatAuditDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const normalizeOutletOption = (option) => ({
  id: String(option?.outlet_id ?? option?.id ?? ""),
  name: String(
    option?.outlet_name || option?.name || option?.bp_outlet_name || "",
  ).trim(),
  address: String(option?.outlet_address || option?.address || "").trim(),
});

const toOutletOptions = (payload) =>
  Array.from(
    (Array.isArray(payload) ? payload : [])
      .map(normalizeOutletOption)
      .filter((option) => option.id && option.name)
      .reduce((accumulator, option) => {
        const key = `${option.name.toLowerCase()}::${option.address.toLowerCase()}`;
        if (!accumulator.has(key)) {
          accumulator.set(key, option);
        }
        return accumulator;
      }, new Map())
      .values(),
  );

export default function SessionCorrectionDrawer({
  isOpen,
  checkInId,
  onClose,
  onSaved,
  t,
  showSuccess,
  showError,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState(null);
  const [outletQuery, setOutletQuery] = useState("");
  const [outletOptions, setOutletOptions] = useState([]);
  const [showOutletSuggestions, setShowOutletSuggestions] = useState(false);
  const outletHelperRef = useRef(null);
  const [form, setForm] = useState({
    outlet_id: "",
    checked_in_date: "",
    checked_in_time: "",
    checked_out_date: "",
    checked_out_time: "",
    checkout_reason: "",
    is_event: false,
  });

  const selectedOutletLabel = useMemo(() => {
    const selected = outletOptions.find(
      (item) => String(item.id) === String(form.outlet_id),
    );
    return selected?.name || outletQuery.trim() || session?.outlet_name || "";
  }, [form.outlet_id, outletOptions, outletQuery, session]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (outletHelperRef.current && !outletHelperRef.current.contains(event.target)) {
        setShowOutletSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !checkInId) {
      setSession(null);
      setOutletQuery("");
      setOutletOptions([]);
      setForm({
        outlet_id: "",
        checked_in_date: "",
        checked_in_time: "",
        checked_out_date: "",
        checked_out_time: "",
        checkout_reason: "",
        is_event: false,
      });
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setSession(null);
        const response = await getAdminSessionById(checkInId);
        if (cancelled) return;
        const loadedSession = response?.data || null;
        const checkedInParts = splitDateTimeValue(loadedSession?.checked_in_at);
        const checkedOutParts = splitDateTimeValue(loadedSession?.checked_out_at);
        setSession(loadedSession);
        setOutletQuery(loadedSession?.outlet_name || "");
        setShowOutletSuggestions(false);
        setForm({
          outlet_id: String(loadedSession?.outlet_id || ""),
          checked_in_date: checkedInParts.date,
          checked_in_time: checkedInParts.time,
          checked_out_date: checkedOutParts.date,
          checked_out_time: checkedOutParts.time,
          checkout_reason: loadedSession?.checkout_reason || "",
          is_event:
            loadedSession?.is_event === true ||
            loadedSession?.is_event === 1 ||
            String(loadedSession?.is_event || "").trim().toLowerCase() === "true" ||
            String(loadedSession?.is_event || "").trim().toLowerCase() === "t" ||
            String(loadedSession?.is_event || "").trim() === "1" ||
            String(loadedSession?.event_details || "").trim().toLowerCase() === "roadshow",
        });

        const outletResponse = await getAdminOutlets({
          q: "",
          limit: 20,
          offset: 0,
        });
        if (cancelled) return;
        setOutletOptions(toOutletOptions(outletResponse?.data));
      } catch (error) {
        if (!cancelled) {
          showError(
            error?.response?.data?.error ||
              t("admin.sessions.corrections.failedToLoad", {
                defaultValue: "Failed to load session details.",
              }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [checkInId, isOpen, showError, t]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const response = await getAdminOutlets({
          q: outletQuery,
          limit: 20,
          offset: 0,
        });
        if (!cancelled) {
          setOutletOptions(toOutletOptions(response?.data));
        }
      } catch (_) {
        if (!cancelled) {
          setOutletOptions([]);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isOpen, outletQuery]);

  const handleSave = async () => {
    const normalizedOutletName = String(outletQuery || "").trim();

    if (!normalizedOutletName) {
      showError("Outlet is required.");
      return;
    }

    if (!form.checked_in_date || !form.checked_in_time) {
      showError("Check-in date and time are required.");
      return;
    }

    if (
      (form.checked_out_date && !form.checked_out_time) ||
      (!form.checked_out_date && form.checked_out_time)
    ) {
      showError("Please provide both check-out date and time, or leave both empty.");
      return;
    }

    const checkedInDateTime =
      form.checked_in_date && form.checked_in_time
        ? `${form.checked_in_date}T${form.checked_in_time}`
        : "";
    const checkedOutDateTime =
      form.checked_out_date && form.checked_out_time
        ? `${form.checked_out_date}T${form.checked_out_time}`
        : "";

    try {
      setSaving(true);
      await updateAdminSessionById(checkInId, {
        outlet_id: String(form.outlet_id || "").trim() || null,
        outlet_name: normalizedOutletName,
        checked_in_at: checkedInDateTime
          ? new Date(checkedInDateTime).toISOString()
          : "",
        checked_out_at: checkedOutDateTime
          ? new Date(checkedOutDateTime).toISOString()
          : "",
        checkout_reason: form.checkout_reason,
        is_event: Boolean(form.is_event),
      });
      showSuccess(
        t("admin.sessions.corrections.saveSuccess", {
          defaultValue: "Session updated successfully.",
        }),
      );
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.sessions.corrections.saveFailed", {
            defaultValue: "Failed to update session.",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={t("admin.sessions.corrections.drawerTitle", {
        defaultValue: "Session Correction",
      })}
      subtitle={session?.promoter_name || ""}
    >
      <div className="admin-correction-drawer">
        {loading ? (
          <div className="admin-correction-skeleton">
            <div className="skeleton-box admin-correction-skeleton-line" />
            <div className="skeleton-box admin-correction-skeleton-line" />
            <div className="skeleton-box admin-correction-skeleton-card" />
          </div>
        ) : (
          <>
            <section className="admin-correction-section">
              <div className="admin-correction-grid">
                <div className="admin-correction-summary">
                  <span>Promoter</span>
                  <strong>{session?.promoter_name || "-"}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Outlet</span>
                  <strong>{session?.outlet_name || "-"}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Status</span>
                  <strong>{session?.status || "-"}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Location Audit</span>
                  <strong>{formatLocationStatusLabel(session?.location_match_status, t)}</strong>
                </div>
              </div>
            </section>

            <section className="admin-correction-section">
              <h5>Session Details</h5>
              <div className="admin-correction-form">
                <div className="admin-correction-selected-helper">
                  <span>Selected outlet</span>
                  <strong>{selectedOutletLabel || "None selected"}</strong>
                </div>

                <label>
                  Outlet Search
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <div className="admin-sessions-helper" ref={outletHelperRef}>
                  <input
                    type="text"
                    className="admin-sessions-input"
                    value={outletQuery}
                    onFocus={() => setShowOutletSuggestions(true)}
                    onChange={(event) => {
                      setOutletQuery(event.target.value);
                      setForm((previous) => ({
                        ...previous,
                        outlet_id: "",
                      }));
                      setShowOutletSuggestions(true);
                    }}
                    placeholder="Search outlet or type a custom outlet name"
                  />
                  <p className="admin-correction-outlet-hint">
                    If outlet is not in database, you can still type and save it manually.
                  </p>
                  {showOutletSuggestions ? (
                    <div className="admin-sessions-suggestions">
                      {outletOptions.length > 0 ? (
                        outletOptions.map((outletOption) => (
                          <button
                            key={outletOption.id}
                            type="button"
                            className="admin-sessions-suggestion-item"
                            onClick={() => {
                              setForm((previous) => ({
                                ...previous,
                                outlet_id: String(outletOption.id),
                              }));
                              setOutletQuery(outletOption.name || "");
                              setShowOutletSuggestions(false);
                            }}
                          >
                            <span>{outletOption.name}</span>
                            {outletOption.address ? (
                              <small>{outletOption.address}</small>
                            ) : null}
                          </button>
                        ))
                      ) : (
                        <div className="admin-sessions-suggestion-empty">
                          No outlet matches found. You can continue typing and save this as a custom outlet.
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <label>
                  Check-in
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <div className="admin-correction-date-time-grid">
                  <div className="admin-correction-date-time-field">
                    <span className="admin-correction-date-time-title">
                      Date
                      <span className="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <input
                      type="date"
                      className="admin-sessions-input"
                      value={form.checked_in_date}
                      onFocus={() => setShowOutletSuggestions(false)}
                      onChange={(event) => {
                        setShowOutletSuggestions(false);
                        setForm((previous) => ({
                          ...previous,
                          checked_in_date: event.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="admin-correction-date-time-field">
                    <span className="admin-correction-date-time-title">
                      Time
                      <span className="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <input
                      type="time"
                      className="admin-sessions-input"
                      value={form.checked_in_time}
                      onFocus={() => setShowOutletSuggestions(false)}
                      onChange={(event) => {
                        setShowOutletSuggestions(false);
                        setForm((previous) => ({
                          ...previous,
                          checked_in_time: event.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>

                <label>Check-out</label>
                <div className="admin-correction-date-time-grid">
                  <div className="admin-correction-date-time-field">
                    <span className="admin-correction-date-time-title">Date</span>
                    <input
                      type="date"
                      className="admin-sessions-input"
                      value={form.checked_out_date}
                      onFocus={() => setShowOutletSuggestions(false)}
                      onChange={(event) => {
                        setShowOutletSuggestions(false);
                        setForm((previous) => ({
                          ...previous,
                          checked_out_date: event.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="admin-correction-date-time-field">
                    <span className="admin-correction-date-time-title">Time</span>
                    <input
                      type="time"
                      className="admin-sessions-input"
                      value={form.checked_out_time}
                      onFocus={() => setShowOutletSuggestions(false)}
                      onChange={(event) => {
                        setShowOutletSuggestions(false);
                        setForm((previous) => ({
                          ...previous,
                          checked_out_time: event.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>

                <label>Checkout Reason</label>
                <select
                  className="admin-sessions-input admin-sessions-select"
                  value={form.checkout_reason}
                  onFocus={() => setShowOutletSuggestions(false)}
                  onChange={(event) => {
                    setShowOutletSuggestions(false);
                    setForm((previous) => ({
                      ...previous,
                      checkout_reason: event.target.value,
                    }));
                  }}
                >
                  <option value="">Not set</option>
                  <option value="break">Break</option>
                  <option value="off_duty">Off Duty</option>
                  <option value="auto_checkout">Auto Checkout</option>
                </select>

                <label>Is Event</label>
                <select
                  className="admin-sessions-input admin-sessions-select"
                  value={form.is_event ? "yes" : "no"}
                  onFocus={() => setShowOutletSuggestions(false)}
                  onChange={(event) => {
                    setShowOutletSuggestions(false);
                    setForm((previous) => ({
                      ...previous,
                      is_event: event.target.value === "yes",
                    }));
                  }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes, Roadshow</option>
                </select>
              </div>
            </section>

            <section className="admin-correction-section">
              <h5>Location Audit</h5>
              <div className="admin-correction-grid">
                <div className="admin-correction-summary">
                  <span>Promoter Latitude</span>
                  <strong>{formatCoordinate(session?.check_in_latitude)}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Promoter Longitude</span>
                  <strong>{formatCoordinate(session?.check_in_longitude)}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Promoter Accuracy</span>
                  <strong>
                    {session?.check_in_accuracy_meters != null
                      ? `${Number(session.check_in_accuracy_meters).toFixed(2)} m`
                      : "-"}
                  </strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Promoter Captured At</span>
                  <strong>{formatAuditDateTime(session?.location_captured_at)}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Check-in Address</span>
                  <strong>{session?.check_in_address || "-"}</strong>
                </div>
                <div className="admin-correction-summary">
                  <span>Distance</span>
                  <strong>
                    {session?.distance_from_outlet_meters != null
                      ? `${Math.round(session.distance_from_outlet_meters)} m`
                      : "-"}
                  </strong>
                </div>
              </div>
            </section>

            <div className="admin-correction-actions admin-correction-actions-single">
              <button
                type="button"
                className="admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}

        {saving ? (
          <div className="admin-correction-overlay" aria-busy="true">
            <div className="admin-reports-export-overlay-spinner"></div>
          </div>
        ) : null}
      </div>
    </DetailDrawer>
  );
}
